/**
 * FhirRealtimeProvider — shared WebSocket subscriptions for in-app notifications
 * and client-portal Tasks/Documents (mirrors MessagingRealtimeProvider).
 *
 * Messaging avoids recipient-scoped Communication criteria because of
 * meta.account compartment gating. Notifications and cross-actor Task creates
 * hit the same issue — use subject=/patient= + tag filters, then refetch.
 */

import type { Bundle, Communication } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react-hooks';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FhirRealtimeContext,
  type FhirRealtimeNotificationHandler,
  type FhirRealtimeRefreshHandler,
} from './fhirRealtimeContext';
import { usePortalSubject } from '../contexts/PortalSubjectContext';
import {
  NOTIF,
  buildNotificationSubscriptionCriteria,
  getCurrentNotificationRecipientReference,
  getNotificationTargetReference,
  isNotificationCommunication,
  isNotificationForRecipient,
  isUnreadNotification,
} from '../lib/notificationFhir';
import {
  buildClientPortalTaskSubscriptionCriteria,
  buildPatientAppointmentSubscriptionCriteria,
  buildPatientDocumentReferenceSubscriptionCriteria,
  buildPatientQuestionnaireResponseSubscriptionCriteria,
} from '../lib/fhirRealtimeCriteria';
import { useFhirSubscription } from './useFhirSubscription';
import { createThreadRefreshCoalescer } from '../pages/messaging/hooks/useMessagingRealtime';

const isDev = import.meta.env.DEV;

export type { FhirRealtimeContextValue, FhirRealtimeNotificationHandler, FhirRealtimeRefreshHandler } from './fhirRealtimeContext';
export { useFhirRealtime, useFhirRealtimeOptional } from './fhirRealtimeContext';

/**
 * Find the Communication in a subscription bundle by iterating all entries.
 * Medplum subscription bundles may include a SubscriptionStatus at entry[0]
 * followed by the triggering resource at a later entry — same pattern as messaging.
 */
function communicationFromBundle(bundle: Bundle): Communication | undefined {
  for (const entry of bundle.entry ?? []) {
    if (entry.resource?.resourceType === 'Communication') {
      return entry.resource as Communication;
    }
  }
  return undefined;
}

/** Lifecycle notifications that imply client-portal data changed (tasks, appointments). */
function notificationImpliesPortalRefresh(comm: Communication): boolean {
  const categoryCode = comm.category?.[0]?.coding?.find(
    (c) => c.system === NOTIF.CATEGORY_SYSTEM,
  )?.code;
  if (categoryCode === 'intake' || categoryCode === 'appointments') return true;
  const targetRef = getNotificationTargetReference(comm)?.reference;
  if (
    targetRef?.startsWith('Task/')
    || targetRef?.startsWith('Appointment/')
    || targetRef?.startsWith('DocumentReference/')
  ) {
    return true;
  }
  return comm.about?.some(
    (a) =>
      a.reference?.startsWith('Task/')
      || a.reference?.startsWith('Appointment/')
      || a.reference?.startsWith('DocumentReference/'),
  ) ?? false;
}

export function FhirRealtimeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const profile = useMedplumProfile();
  const { activePatientId } = usePortalSubject();

  const recipientRef = useMemo(
    () => getCurrentNotificationRecipientReference(profile, activePatientId),
    [profile, activePatientId],
  );

  const portalPatientId = useMemo(() => {
    if (activePatientId) return activePatientId;
    if (profile?.resourceType === 'Patient' && profile.id) return profile.id;
    return undefined;
  }, [activePatientId, profile]);

  const notificationCriteria = useMemo(
    () => (recipientRef ? buildNotificationSubscriptionCriteria(recipientRef) : undefined),
    [recipientRef],
  );

  const taskCriteria = useMemo(
    () => (portalPatientId ? buildClientPortalTaskSubscriptionCriteria(portalPatientId) : undefined),
    [portalPatientId],
  );

  const documentCriteria = useMemo(
    () => (portalPatientId ? buildPatientDocumentReferenceSubscriptionCriteria(portalPatientId) : undefined),
    [portalPatientId],
  );

  const qrCriteria = useMemo(
    () => (portalPatientId ? buildPatientQuestionnaireResponseSubscriptionCriteria(portalPatientId) : undefined),
    [portalPatientId],
  );

  const appointmentCriteria = useMemo(
    () => (portalPatientId ? buildPatientAppointmentSubscriptionCriteria(portalPatientId) : undefined),
    [portalPatientId],
  );

  const notificationListeners = useRef<Set<FhirRealtimeRefreshHandler>>(new Set());
  const notificationReceivedListeners = useRef<Set<FhirRealtimeNotificationHandler>>(new Set());
  const portalListeners = useRef<Set<FhirRealtimeRefreshHandler>>(new Set());

  const notificationCoalescer = useRef(createThreadRefreshCoalescer(() => {
    for (const handler of notificationListeners.current) {
      try {
        handler();
      } catch (err) {
        console.error('[FhirRealtime] Notification listener error:', err);
      }
    }
  }));

  const portalCoalescer = useRef(createThreadRefreshCoalescer(() => {
    for (const handler of portalListeners.current) {
      try {
        handler();
      } catch (err) {
        console.error('[FhirRealtime] Client portal listener error:', err);
      }
    }
  }));

  const handleNotificationBundle = useCallback((bundle: Bundle) => {
    if (!recipientRef) return;
    const comm = communicationFromBundle(bundle);
    if (!comm) return;
    if (isDev) {
      console.log(
        '[FhirRealtime][Notifications] WS event — Communication', comm.id,
        'recipient:', comm.recipient?.map((r) => r.reference).join(', '),
        'category:', comm.category?.[0]?.coding?.[0]?.code,
      );
    }
    if (!isNotificationCommunication(comm)) {
      if (isDev) console.log('[FhirRealtime][Notifications] Filtered: not an in-app notification');
      return;
    }
    if (!isNotificationForRecipient(comm, recipientRef)) {
      if (isDev) console.log('[FhirRealtime][Notifications] Filtered: not for recipient', recipientRef);
      return;
    }
    if (isUnreadNotification(comm)) {
      for (const handler of notificationReceivedListeners.current) {
        try {
          handler(comm);
        } catch (err) {
          console.error('[FhirRealtime] Notification received listener error:', err);
        }
      }
    }
    notificationCoalescer.current.enqueue(null);
    if (notificationImpliesPortalRefresh(comm)) {
      if (isDev) console.log('[FhirRealtime][Notifications] Also triggering client portal refresh');
      portalCoalescer.current.enqueue(null);
    }
  }, [recipientRef]);

  const handlePortalBundle = useCallback((_bundle: Bundle) => {
    portalCoalescer.current.enqueue(null);
  }, []);

  const handleNotificationReconnect = useCallback(() => {
    notificationCoalescer.current.enqueue(null);
  }, []);

  const handlePortalReconnect = useCallback(() => {
    portalCoalescer.current.enqueue(null);
  }, []);

  useEffect(() => {
    if (!isDev || !notificationCriteria) return;
    console.log('[FhirRealtime] Notification subscription — criteria:', notificationCriteria);
  }, [notificationCriteria]);

  useEffect(() => {
    if (!isDev || !taskCriteria) return;
    console.log('[FhirRealtime] Client portal subscriptions — task:', taskCriteria);
  }, [taskCriteria]);

  const { connected: notificationsConnected } = useFhirSubscription(
    notificationCriteria,
    { onBundle: handleNotificationBundle, onReconnect: handleNotificationReconnect },
    '[FhirRealtime][Notifications]',
  );

  const { connected: tasksConnected } = useFhirSubscription(
    taskCriteria,
    { onBundle: handlePortalBundle, onReconnect: handlePortalReconnect },
    '[FhirRealtime][Task]',
  );

  useFhirSubscription(
    documentCriteria,
    { onBundle: handlePortalBundle, onReconnect: handlePortalReconnect },
    '[FhirRealtime][DocumentReference]',
  );

  useFhirSubscription(
    qrCriteria,
    { onBundle: handlePortalBundle, onReconnect: handlePortalReconnect },
    '[FhirRealtime][QuestionnaireResponse]',
  );

  useFhirSubscription(
    appointmentCriteria,
    { onBundle: handlePortalBundle, onReconnect: handlePortalReconnect },
    '[FhirRealtime][Appointment]',
  );

  const onNotificationRefresh = useCallback((handler: FhirRealtimeRefreshHandler) => {
    notificationListeners.current.add(handler);
    return () => {
      notificationListeners.current.delete(handler);
    };
  }, []);

  const onNotificationReceived = useCallback((handler: FhirRealtimeNotificationHandler) => {
    notificationReceivedListeners.current.add(handler);
    return () => {
      notificationReceivedListeners.current.delete(handler);
    };
  }, []);

  const onClientPortalRefresh = useCallback((handler: FhirRealtimeRefreshHandler) => {
    portalListeners.current.add(handler);
    return () => {
      portalListeners.current.delete(handler);
    };
  }, []);

  const value = useMemo(
    () => ({
      onNotificationRefresh,
      onNotificationReceived,
      onClientPortalRefresh,
      notificationsConnected,
      clientPortalConnected: tasksConnected,
    }),
    [onNotificationRefresh, onNotificationReceived, onClientPortalRefresh, notificationsConnected, tasksConnected],
  );

  return (
    <FhirRealtimeContext.Provider value={value}>
      {children}
    </FhirRealtimeContext.Provider>
  );
}
